import { Transition } from '@headlessui/react'
import { Fragment, ReactElement, useEffect, useRef, useState } from 'react'
import DrawerHeaderV2 from './DrawerHeaderV2'
import Spinner from 'app/components/Spinner'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import { useIsMobile } from 'app/hooks/useMediaQuery'

type DrawerV2Props = {
  open?: boolean
  title?: string
  subheader?: React.ReactElement<{ isExpanded?: boolean }>
  banner?: React.ReactElement<{ isExpanded?: boolean }>
  leftContent?: React.ReactElement
  rightContent?: React.ReactElement
  onClose: () => void
  nextItem?: () => void
  previousItem?: () => void
}

export default function DrawerV2({
  open,
  title,
  subheader,
  banner,
  leftContent,
  rightContent,
  onClose,
  nextItem,
  previousItem,
}: DrawerV2Props): ReactElement | null {
  let [expand, setExpand] = useState<boolean>(true)
  const ref = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  // On mobile, hold the heavy body until the open slide finishes. Rendering the
  // tabs/activity panel during the slide janks the animation (closing is smooth
  // because the content is already painted). Desktop renders immediately.
  const [entered, setEntered] = useState(false)
  const showBody = !isMobile || entered
  useEscapeListener(() => onClose(), open)

  useEffect(() => {
    // Where the press started. Selecting text inside the drawer and releasing
    // past its edge fires `mouseup` on an outside element, which read as an
    // outside click and closed the drawer mid-drag. Only a press that both
    // starts and ends outside counts. `pointerdown` still closes immediately
    // when it lands outside, so the Radix-trigger case below is unaffected.
    let pressStartedInside = false

    const startedInsideDrawer = (target: EventTarget | null) =>
      target instanceof Element &&
      (!!ref.current?.contains(target) ||
        !!target.closest(`[data-floating-ui-portal]`) ||
        !!target.closest(`[data-radix-popper-content-wrapper]`) ||
        !!target.closest(`[role="dialog"]`) ||
        !!target.closest(`[data-headlessui-portal]`) ||
        // Another record's link swaps the drawer's contents in its own click handler.
        !!target.closest(`[data-opens-drawer]`) ||
        // The alert row floats over the page, drawer included. Dismissing a card
        // is a press on the row, not on the drawer underneath it.
        !!target.closest(`[data-floating-layer]`))

    const handleClick = (event: Event) => {
      if (event.type === `pointerdown`) {
        pressStartedInside = startedInsideDrawer(event.target)
      } else {
        const startedInside = pressStartedInside
        pressStartedInside = false
        if (startedInside) return
      }
      const target = event.target
      // Note the special condition: if the click is on a portal then close the drawer, UNLESS the portal contains a ContactModalTable component
      if (
        target instanceof HTMLElement &&
        ref.current &&
        !ref.current.contains(target) &&
        !target.closest(`[data-floating-ui-portal]`) &&
        !target.closest(`[data-radix-popper-content-wrapper]`) &&
        !target.closest(`[role="dialog"]`) &&
        !target.closest(`[data-opens-drawer]`) &&
        !target.closest(`[data-floating-layer]`) &&
        (!target.closest(`[data-headlessui-portal]`) ||
          (target.closest(`[data-headlessui-portal]`) &&
            target.closest(`[data-headlessui-portal]`)?.querySelector(`[x-component='ContactModalTable']`)))
      ) {
        onClose()
      }
    }

    // Listen on both `pointerdown` AND `mouseup` so we catch all outside-click
    // patterns:
    //   - `mouseup` covers plain buttons / links — the original behavior.
    //   - `pointerdown` is required because Radix triggers (DropdownMenu,
    //     Popover, etc.) call `preventDefault()` on `pointerdown`, which
    //     suppresses the subsequent `mouseup`. Without pointerdown here,
    //     clicking a Radix dropdown trigger behind the drawer wouldn't fire
    //     this handler and the drawer wouldn't close. Pointerdown fires
    //     before Radix's preventDefault can have an effect, so it always
    //     reaches us. Same de-dupe logic applies either way.
    window.addEventListener(`pointerdown`, handleClick)
    window.addEventListener(`mouseup`, handleClick)
    return () => {
      window.removeEventListener(`pointerdown`, handleClick)
      window.removeEventListener(`mouseup`, handleClick)
    }
  }, [ref, onClose])

  return (
    <Transition
      as={Fragment}
      // `appear` runs the enter transition on the initial mount as well as
      // on `show` flipping from false→true. The Tasks page mounts the
      // ContactDrawer the moment the user clicks "Open profile" (so the
      // drawer can show a spinner while the contact loads), which means
      // `show` is already true when the Transition first paints — without
      // `appear` the enter classes wouldn't run and the drawer would just
      // pop in. With it, the translate-x-full → translate-x-0 slide plays.
      appear
      enter="transform transition ease-out duration-[300ms]"
      enterFrom="opacity-0 translate-x-full"
      enterTo="opacity-100 translate-x-0"
      leave="transform transition ease-in duration-[300ms]"
      leaveFrom="opacity-100 translate-x-0"
      leaveTo="opacity-0 translate-x-full"
      show={open}
      afterEnter={() => setEntered(true)}
      afterLeave={() => setEntered(false)}
    >
      {/* `z-[35]` keeps the contact drawer above the task Sheet (`z-30`) so
          opening the contact profile from inside a Sheet layers correctly on
          top of it. Still sits below the global Headless UI Modal (`z-40`) so
          modals invoked from the contact drawer (e.g. the click-to-call
          modal) appear on top of everything. */}
      {/* Full-screen takeover on mobile (< md): full width + dvh height so the
          contact profile fills the phone. On desktop it stays a right-anchored
          panel sized by its content. The drop shadow is dropped on mobile
          because repainting a full-screen box-shadow every frame makes the open
          slide janky (and a full-screen drawer needs no shadow anyway). */}
      <div
        ref={ref}
        x-component-name="Drawer"
        className="fixed right-0 top-0 z-[35] w-full md:w-auto md:shadow-elevation-8"
      >
        <div
          tabIndex={-1}
          className="flex h-dvh w-full flex-col bg-surface-muted pt-[env(safe-area-inset-top)] text-left md:h-screen md:w-auto md:pt-0"
        >
          <DrawerHeaderV2
            title={title}
            subheader={subheader}
            isExpanded={expand}
            onToggleExpand={() => setExpand((prev) => !prev)}
            onClose={onClose}
            onPrevious={previousItem}
            onNext={nextItem}
            canNavigate={!!(nextItem && previousItem)}
            banner={banner}
          />
          {/* On mobile a single full-width pane fills the height and scrolls its
              own content (the tabbed contact view); on desktop the two panes sit
              side by side with their own overflow. */}
          <div className="flex grow flex-col overflow-hidden bg-surface-muted md:flex-row">
            {showBody ? (
              <>
                <div className="flex min-h-0 w-full grow md:w-auto md:grow-0">{leftContent}</div>
                {expand && rightContent && <div className="flex w-full grow pr-1 md:w-auto">{rightContent}</div>}
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <Spinner size={20} />
              </div>
            )}
          </div>
        </div>
      </div>
    </Transition>
  )
}
